import { useState, useMemo } from "react";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";
import { useQueries } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useAssignments } from "@/hooks/use-form-mutations";
import { useProjects } from "@/hooks/use-projects";
import { useOrgId } from "@/lib/org-context";
import { getProject } from "@/lib/time-api";
import type { Task } from "@/types/time";

interface AssignmentSectionProps {
  formId: string;
}

interface TaskWithProject extends Task {
  projectName: string;
  projectColor: string | null;
}

export function AssignmentSection({ formId }: AssignmentSectionProps) {
  const { assignments, add, remove } = useAssignments(formId);
  const { projects } = useProjects();
  const orgId = useOrgId();
  const [open, setOpen] = useState(false);

  const activeProjects = projects.filter((p) => !p.isArchived);

  // Fetch tasks for all active projects
  const projectQueries = useQueries({
    queries: activeProjects.map((p) => ({
      queryKey: ["project", orgId, p.id],
      queryFn: () => getProject(p.id),
      staleTime: 30_000,
    })),
  });

  const allTasks = useMemo(() => {
    const tasks: TaskWithProject[] = [];
    projectQueries.forEach((q, i) => {
      if (q.data?.tasks) {
        const project = activeProjects[i];
        for (const t of q.data.tasks) {
          if (!t.isArchived) {
            tasks.push({
              ...t,
              projectName: project.name,
              projectColor: project.color,
            });
          }
        }
      }
    });
    return tasks;
  }, [projectQueries, activeProjects]);

  const assignedIds = useMemo(() => {
    const set = new Set<string>();
    for (const a of assignments) {
      set.add(`${a.assignableType}:${a.assignableId}`);
    }
    return set;
  }, [assignments]);

  const unassignedProjects = activeProjects.filter(
    (p) => !assignedIds.has(`project:${p.id}`)
  );
  const unassignedTasks = allTasks.filter(
    (t) => !assignedIds.has(`task:${t.id}`)
  );

  async function handleAssign(
    type: "project" | "task",
    id: string
  ) {
    setOpen(false);
    try {
      await add.mutateAsync({ assignableType: type, assignableId: id });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to assign");
    }
  }

  async function handleRemove(assignmentId: string) {
    try {
      await remove.mutateAsync(assignmentId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to remove assignment"
      );
    }
  }

  function chipLabel(a: { assignableType: string; assignableId: string }) {
    if (a.assignableType === "project") {
      const project = projects.find((p) => p.id === a.assignableId);
      return { label: project?.name ?? a.assignableId, color: project?.color };
    }
    const task = allTasks.find((t) => t.id === a.assignableId);
    return {
      label: task ? `${task.name} · ${task.projectName}` : a.assignableId,
      color: task?.projectColor,
    };
  }

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <Label>Assignments</Label>
      <p className="text-xs text-muted-foreground">
        This form will appear at clock-out for members who logged time on:
      </p>

      {assignments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {assignments.map((a) => {
            const { label, color } = chipLabel(a);
            return (
              <span
                key={a.id}
                className="inline-flex items-center gap-1.5 rounded-md border bg-secondary px-2 py-1 text-xs"
              >
                <span
                  className="inline-block h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: color ?? "#94A3B8" }}
                />
                {a.assignableType === "task" && (
                  <span className="text-muted-foreground">Task:</span>
                )}
                {label}
                <button
                  onClick={() => handleRemove(a.id)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Assign to project or task...
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search..." />
            <CommandList>
              <CommandEmpty>No projects or tasks found.</CommandEmpty>
              {unassignedProjects.length > 0 && (
                <CommandGroup heading="Projects">
                  {unassignedProjects.map((p) => (
                    <CommandItem
                      key={p.id}
                      value={`project-${p.name}`}
                      onSelect={() => handleAssign("project", p.id)}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block h-2 w-2 rounded-full shrink-0"
                          style={{ backgroundColor: p.color ?? "#94A3B8" }}
                        />
                        {p.name}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {unassignedProjects.length > 0 && unassignedTasks.length > 0 && (
                <CommandSeparator />
              )}
              {unassignedTasks.length > 0 && (
                <CommandGroup heading="Tasks">
                  {unassignedTasks.map((t) => (
                    <CommandItem
                      key={t.id}
                      value={`task-${t.name}-${t.projectName}`}
                      onSelect={() => handleAssign("task", t.id)}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block h-2 w-2 rounded-full shrink-0"
                          style={{ backgroundColor: t.projectColor ?? "#94A3B8" }}
                        />
                        <span>
                          {t.name}
                          <span className="ml-1 text-muted-foreground">
                            · {t.projectName}
                          </span>
                        </span>
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
